import { useTranslation } from 'react-i18next';
import type { CapiPlp } from 'types/capi/plp';

import { InternalLink } from '~/components/Button';
import usePageData from '~/hooks/usePageData';
import gtm from '~/services/gtm.client';

const PAGE_POSITION = 'No Results';

const clearLinkClassName = 'text-caption-grey hover:underline';

export default function NoResults() {
  const { t } = useTranslation('plp');
  const plp = usePageData<CapiPlp>();

  const [allOption, ...tabOptions] = plp.filter_options;
  const activeTab = tabOptions.find((option) => option.active);
  const canSeeAll = !!activeTab && !!allOption?.has_results;

  function onClick(name: string) {
    gtm.sendUserInteractionEvent(name, PAGE_POSITION);
  }

  function onSeeAllClick() {
    if (!allOption) return;

    gtm.sendPlpProductFilterEvent({
      filterName: allOption.text,
      filterValue: allOption.text,
      pagePosition: PAGE_POSITION
    });
  }

  return (
    <div className="flex flex-col items-center gap-3 py-20 text-center md:py-32">
      <h2 className="text-title-sm text-grey-hide">
        {canSeeAll
          ? t('noResultsInTab', { tab: activeTab.text })
          : t('noResults')}
      </h2>
      {canSeeAll && (
        <InternalLink
          className="w-full leading-8 md:w-48"
          onClick={onSeeAllClick}
          rel="nofollow"
          to={allOption.path}
          variant="forest"
        >
          {t('seeAllResults')}
        </InternalLink>
      )}
      {(plp.clear_filters_path || plp.clear_search_path) && (
        <div className="flex items-center justify-center gap-4">
          {plp.clear_filters_path && (
            <InternalLink
              className={clearLinkClassName}
              onClick={() => onClick('Clear all filters')}
              rel="nofollow"
              to={plp.clear_filters_path}
              variant="none"
            >
              {t('clearFilters')}
            </InternalLink>
          )}
          {plp.clear_filters_path && plp.clear_search_path && (
            <span aria-hidden className="text-grey-hide">
              •
            </span>
          )}
          {plp.clear_search_path && (
            <InternalLink
              className={clearLinkClassName}
              onClick={() => onClick('Clear search')}
              rel="nofollow"
              to={plp.clear_search_path}
              variant="none"
            >
              {t('clearSearch')}
            </InternalLink>
          )}
        </div>
      )}
    </div>
  );
}
